import type { VideoDisplayRect } from './poi-projection'

export type PayloadInteractionMode =
  'frame-zoom' |
  'screen-drag' |
  'aim' |
  'point-focus' |
  'ir-point' |
  'ir-area'

export interface PayloadInteractionPoint {
  x: number
  y: number
}

export interface PayloadInteractionRect extends PayloadInteractionPoint {
  width: number
  height: number
}

export interface ScreenDragSpeed {
  pitchSpeed: number
  yawSpeed: number
}

export function hasScreenDragSpeed (speed: ScreenDragSpeed): boolean {
  return speed.pitchSpeed !== 0 || speed.yawSpeed !== 0
}

function clamp (value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

export function normalizeVideoPoint (
  containerX: number,
  containerY: number,
  videoRect: VideoDisplayRect
): PayloadInteractionPoint | null {
  if (!Number.isFinite(containerX) || !Number.isFinite(containerY) || videoRect.width <= 0 || videoRect.height <= 0) {
    return null
  }
  const x = (containerX - videoRect.left) / videoRect.width
  const y = (containerY - videoRect.top) / videoRect.height
  if (x < 0 || x > 1 || y < 0 || y > 1) {
    return null
  }
  return { x, y }
}

export function normalizeVideoRect (
  startX: number,
  startY: number,
  endX: number,
  endY: number,
  videoRect: VideoDisplayRect,
  minimumSize = 0.01
): PayloadInteractionRect | null {
  if (videoRect.width <= 0 || videoRect.height <= 0) {
    return null
  }
  const left = clamp(Math.min(startX, endX), videoRect.left, videoRect.left + videoRect.width)
  const right = clamp(Math.max(startX, endX), videoRect.left, videoRect.left + videoRect.width)
  const top = clamp(Math.min(startY, endY), videoRect.top, videoRect.top + videoRect.height)
  const bottom = clamp(Math.max(startY, endY), videoRect.top, videoRect.top + videoRect.height)
  const width = (right - left) / videoRect.width
  const height = (bottom - top) / videoRect.height
  if (width < minimumSize || height < minimumSize) {
    return null
  }
  return {
    x: clamp((left - videoRect.left) / videoRect.width, 0, 1),
    y: clamp((top - videoRect.top) / videoRect.height, 0, 1),
    width: clamp(width, 0, 1),
    height: clamp(height, 0, 1),
  }
}

export function mapDragToSpeed (
  startX: number,
  startY: number,
  currentX: number,
  currentY: number,
  videoRect: VideoDisplayRect,
  maxPitchSpeed: number,
  maxYawSpeed: number,
  deadZone = 0.02
): ScreenDragSpeed {
  if (videoRect.width <= 0 || videoRect.height <= 0) {
    return { pitchSpeed: 0, yawSpeed: 0 }
  }
  let horizontal = clamp((currentX - startX) / (videoRect.width / 2), -1, 1)
  let vertical = clamp((currentY - startY) / (videoRect.height / 2), -1, 1)
  if (Math.abs(horizontal) < deadZone) {
    horizontal = 0
  }
  if (Math.abs(vertical) < deadZone) {
    vertical = 0
  }
  return {
    pitchSpeed: Number((-vertical * maxPitchSpeed).toFixed(3)),
    yawSpeed: Number((horizontal * maxYawSpeed).toFixed(3)),
  }
}

type SendScreenDrag = (speed: ScreenDragSpeed) => Promise<unknown>

/**
 * Keeps screen-drag service requests at a fixed maximum frequency, never has
 * more than one request in flight, and prioritizes the final zero-speed stop.
 */
export class ScreenDragCommandPublisher {
  private readonly send: SendScreenDrag
  private readonly intervalMs: number
  private readonly now: () => number
  private pending: ScreenDragSpeed | null = null
  private sending = false
  private active = false
  private stopRequested = false
  private timer: ReturnType<typeof setTimeout> | undefined
  private lastSentAt = 0
  private stopWaiters: Array<() => void> = []

  constructor (
    send: SendScreenDrag,
    intervalMs = 100,
    now: () => number = () => Date.now()
  ) {
    this.send = send
    this.intervalMs = intervalMs
    this.now = now
  }

  update (speed: ScreenDragSpeed) {
    this.active = true
    this.stopRequested = false
    this.pending = speed
    this.schedule()
  }

  stop (): Promise<void> {
    if (!this.active && !this.stopRequested) {
      return Promise.resolve()
    }
    this.active = false
    this.pending = null
    this.stopRequested = true
    this.clearTimer()
    const promise = new Promise<void>(resolve => this.stopWaiters.push(resolve))
    this.flushStop()
    return promise
  }

  dispose () {
    this.clearTimer()
    return this.stop()
  }

  private schedule () {
    if (this.sending || this.stopRequested || !this.pending || this.timer !== undefined) {
      return
    }
    const delay = Math.max(0, this.intervalMs - (this.now() - this.lastSentAt))
    this.timer = setTimeout(() => {
      this.timer = undefined
      this.flushPending()
    }, delay)
  }

  private async flushPending () {
    if (this.sending || this.stopRequested || !this.pending) {
      if (this.stopRequested) {
        this.flushStop()
      }
      return
    }
    const speed = this.pending
    this.pending = null
    this.sending = true
    try {
      await this.send(speed)
    } catch (_error) {
      // The shared HTTP layer already surfaces command failures.
    } finally {
      this.sending = false
      this.lastSentAt = this.now()
      if (this.stopRequested) {
        this.flushStop()
      } else {
        this.schedule()
      }
    }
  }

  private async flushStop () {
    if (!this.stopRequested || this.sending) {
      return
    }
    this.stopRequested = false
    this.sending = true
    try {
      await this.send({ pitchSpeed: 0, yawSpeed: 0 })
    } catch (_error) {
      // The shared HTTP layer already surfaces command failures.
    } finally {
      this.sending = false
      this.lastSentAt = this.now()
      this.stopWaiters.splice(0).forEach(resolve => resolve())
      this.schedule()
    }
  }

  private clearTimer () {
    if (this.timer !== undefined) {
      clearTimeout(this.timer)
      this.timer = undefined
    }
  }
}
